import {
  BoxProps,
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputProps,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormikHandlers } from "formik";
import { ReactElement, ReactNode } from "react";

import { MemoHelper, memoWithReversedArgs } from "@/common/helpers/memo";
import { BaseFormikValues, ValidationErrors } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { GeneralNationalIdentityNumberInputDto } from "@/core/api/generated/v1";

import GeneralNationalIdentityNumberInput from "../Entity/General/Input/GeneralNationalIdentityNumberInput";
import GeneralValidationError from "../Error/GeneralValidationError";
import PhoneInput from "../Form/Input/PhoneInput";
import AppIcon, { AppIconProps } from "../Icons/AppIcon";
import BaseContactCard from "./BaseContactCard";

const defaultDisplayProps = {
  firstName: true,
  lastName: true,
  email: true,
  phoneNumber: true,
  nationalIdentityNumber: false,
  isPrimary: false,
};

export interface ContactData {
  id?: string;
  tempId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  nationalIdentityNumber?: GeneralNationalIdentityNumberInputDto;
  isPrimary?: boolean;
}

export interface ContactInputProps {
  value: ContactData | null | undefined;
  action?: ReactNode;
  secondaryIcon?: (props: Partial<AppIconProps>) => ReactNode;
  errors?: ValidationErrors<ContactData> & ValidationErrors<BaseFormikValues>;
  inputsProps?: {
    /** Applied to all inputs */
    all?: {
      size?: InputProps["size"];
      onBlur?: InputProps["onBlur"] & CheckboxProps["onBlur"];
    };
  } & Partial<
    Record<
      keyof ContactData,
      {
        name?: InputProps["name"];
        required?: InputProps["required"];
        disabled?: InputProps["disabled"];
        error?: InputProps["error"];
        helperText?: InputProps["error"];
        // onChange?: InputProps["onChange"] & CheckboxProps["onChange"];
        onBlur?: InputProps["onBlur"] & CheckboxProps["onBlur"];
      }
    >
  >;
  displayProps?: Partial<typeof defaultDisplayProps>;
  sx?: BoxProps["sx"];
  children?: ReactElement;
  onChange: (newValue: ContactData) => void | FormikHandlers["handleChange"];
}

export default memoWithReversedArgs(
  MemoHelper.arePropsEqual.factory<ContactInputProps>({
    isExcludeFunctionProps: true,
    excludeProps: (t) => [t.action.$path, t.inputsProps.$path, t.displayProps.$path, t.sx.$path],
    deepCompareProps: (t) => [t.value.$path],
  }),
  function ContactInput({
    value,
    action,
    secondaryIcon,
    errors,
    inputsProps,
    displayProps,
    sx,
    children,
    onChange,
  }: ContactInputProps) {
    displayProps = {
      ...defaultDisplayProps,
      ...displayProps,
    };

    return (
      <BaseContactCard sx={sx}>
        <Stack direction='row' gap={1}>
          <Stack direction='column' gap={1}>
            <AppIcon of='person' fontSize='small' />

            {secondaryIcon &&
              secondaryIcon({
                fontSize: "small",
              })}
          </Stack>

          <Stack direction='column' gap={1.5} sx={{ flexGrow: 1 }}>
            {displayProps?.firstName && (
              <TextField
                error={Boolean(errors?.firstName)}
                fullWidth
                helperText={
                  errors?.firstName && ValidationHelper.getErrorsAsString(errors?.firstName)
                }
                label='First name'
                margin='none'
                size={inputsProps?.all?.size || "small"}
                type='text'
                value={value?.firstName || ""}
                variant='outlined'
                slotProps={{
                  inputLabel: {
                    sx: {
                      background: "inherit",
                    },
                  },
                }}
                onBlur={inputsProps?.all?.onBlur}
                onChange={(e) =>
                  onChange &&
                  onChange({
                    ...value,
                    firstName: e.target.value,
                  })
                }
                {...inputsProps?.firstName}
              />
            )}

            {displayProps?.lastName && (
              <TextField
                error={Boolean(errors?.lastName)}
                fullWidth
                helperText={
                  errors?.lastName && ValidationHelper.getErrorsAsString(errors?.lastName)
                }
                label='Last name'
                margin='none'
                size={inputsProps?.all?.size || "small"}
                type='text'
                value={value?.lastName || ""}
                variant='outlined'
                slotProps={{
                  inputLabel: {
                    sx: {
                      background: "inherit",
                    },
                  },
                }}
                onBlur={inputsProps?.all?.onBlur}
                onChange={(e) =>
                  onChange &&
                  onChange({
                    ...value,
                    lastName: e.target.value,
                  })
                }
                {...inputsProps?.lastName}
              />
            )}

            {displayProps?.email && (
              <TextField
                error={Boolean(errors?.email)}
                fullWidth
                helperText={errors?.email && ValidationHelper.getErrorsAsString(errors?.email)}
                label='Email'
                margin='none'
                size={inputsProps?.all?.size || "small"}
                type='email'
                value={value?.email || ""}
                variant='outlined'
                slotProps={{
                  inputLabel: {
                    sx: {
                      background: "inherit",
                    },
                  },
                }}
                onBlur={inputsProps?.all?.onBlur}
                onChange={(e) =>
                  onChange &&
                  onChange({
                    ...value,
                    email: e.target.value,
                  })
                }
                {...inputsProps?.email}
              />
            )}

            {displayProps?.phoneNumber && (
              <PhoneInput
                error={Boolean(errors?.phoneNumber)}
                fullWidth
                helperText={
                  errors?.phoneNumber && ValidationHelper.getErrorsAsString(errors?.phoneNumber)
                }
                label='Phone number'
                margin='none'
                value={value?.phoneNumber || ""}
                variant='outlined'
                color='primary'
                placeholder='Phone number'
                size={inputsProps?.all?.size || "small"}
                slotProps={{
                  inputLabel: {
                    sx: {
                      background: "inherit",
                    },
                  },
                }}
                onBlur={inputsProps?.all?.onBlur}
                onChange={(e, phone, iso) => {
                  onChange &&
                    onChange({
                      ...value,
                      phoneNumber: phone,
                    });
                }}
                {...inputsProps?.phoneNumber}
              />
            )}

            {displayProps?.nationalIdentityNumber && (
              <GeneralNationalIdentityNumberInput
                value={value?.nationalIdentityNumber}
                margin='none'
                size={inputsProps?.all?.size || "small"}
                fullWidth
                error={Boolean(errors?.nationalIdentityNumber)}
                helperText={
                  errors?.nationalIdentityNumber &&
                  ValidationHelper.getErrorsAsString(errors?.nationalIdentityNumber)
                }
                slotProps={{
                  inputLabel: {
                    sx: {
                      background: "inherit",
                    },
                  },
                }}
                onChange={(newValue) => {
                  onChange &&
                    onChange({
                      ...value,
                      nationalIdentityNumber: newValue || undefined,
                    });
                }}
                onBlur={inputsProps?.all?.onBlur}
                {...inputsProps?.nationalIdentityNumber}
              />
            )}

            {displayProps?.isPrimary && (
              <FormControl margin='none' fullWidth error={Boolean(errors?.isPrimary)}>
                <FormControlLabel
                  label={
                    <Typography component='span' sx={{ display: "flex" }}>
                      Primary contact
                    </Typography>
                  }
                  control={
                    <Checkbox
                      checked={value?.isPrimary ?? false}
                      size={inputsProps?.all?.size || "small"}
                      onChange={(e) => {
                        onChange &&
                          onChange({
                            ...value,
                            isPrimary: e.target.checked,
                          });
                      }}
                      onBlur={inputsProps?.all?.onBlur}
                      {...inputsProps?.isPrimary}
                    />
                  }
                />
                {errors?.isPrimary && (
                  <FormHelperText error>
                    {ValidationHelper.getErrorsAsString(errors?.isPrimary)}
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {children && children}
          </Stack>

          <Stack direction='column' sx={{ justifyContent: "flex-start" }}>
            {action && action}
          </Stack>

          <GeneralValidationError sx={{ my: 1 }} errors={errors} />
        </Stack>
      </BaseContactCard>
    );
  },
);
